import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { usePyodide, RunResult } from '../hooks/usePyodide';
import { projectService, ProjectApiError } from '../services/project.service';
import { useAuthStore } from '../store/authStore';
import { useFeatures } from '../hooks/useFeatures';
import { Project, SubmitProjectResponse, getProjectCategory } from '../types/project.types';
import { CodeEditorPanel } from '../components/code-editor/CodeEditorPanel';
import { OutputPanel } from '../components/code-editor/OutputPanel';
import { ScienceProjectWorkspace } from '../components/projects/workspaces/ScienceProjectWorkspace';
import { MathProjectWorkspace } from '../components/projects/workspaces/MathProjectWorkspace';
import { EnglishProjectWorkspace } from '../components/projects/workspaces/EnglishProjectWorkspace';
import { TurkishProjectWorkspace } from '../components/projects/workspaces/TurkishProjectWorkspace';
import '../styles/code-editor.css';
import '../styles/projects.css';
import { PremiumProjectInfoCard } from '../components/projects/PremiumProjectModal';
import { usePlanUsage } from '../hooks/usePlanUsage';
import { PlanLimitBar } from '../components/common/PlanLimitBar';

export function ProjectWorkspacePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const profile = useAuthStore((s) => s.profile);
  const fetchProfile = useAuthStore((s) => s.fetchProfile);
  const { isReady, isLoading: pyodideLoading, loadError, runPython } = usePyodide();
  const { hasFeature } = useFeatures();
  const { isAtLimit, usage, refetch: refetchUsage } = usePlanUsage();

  const [project, setProject] = useState<Project | null>(null);
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [loadProjectError, setLoadProjectError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitProjectResponse | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    async function load() {
      setIsLoadingProject(true);
      setLoadProjectError(null);
      try {
        const { data, error } = await supabase.from('projects').select('*').eq('id', projectId!).single();
        if (error) throw error;
        if (cancelled) return;
        setProject(data);
        setCode(data.starter_code ?? '');
      } catch (err) {
        if (!cancelled) setLoadProjectError(err instanceof Error ? err.message : 'Proje yüklenemedi.');
      } finally {
        if (!cancelled) setIsLoadingProject(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  async function handleRun() {
    if (!isReady) return;
    setIsRunning(true);
    const result = await runPython(code);
    setRunResult(result);
    setIsRunning(false);
  }

  function handleReset() {
    setCode(project?.starter_code ?? '');
    setRunResult(null);
    setSubmitResult(null);
    setSubmitError(null);
  }

  function handleClearOutput() {
    setRunResult(null);
  }

  async function handleSubmit() {
    if (!project) return;
    if (!code || !code.trim()) {
      setSubmitError('Lütfen projeyi göndermeden önce çalışmanızı doldurun.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await projectService.submitProject(project.id, code, runResult?.output ?? null);
      setSubmitResult(result);
      await fetchProfile();
      setTimeout(refetchUsage, 2000); // limit sayıcısını güncelle
    } catch (err) {
      setSubmitError(err instanceof ProjectApiError ? err.message : 'Proje gönderilemedi.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoadingProject) return <div className="courses-loading">Yükleniyor...</div>;
  if (loadProjectError) return <div className="courses-error">{loadProjectError}</div>;
  if (!project) return null;

  const isAdmin = profile?.role === 'admin';
  const canAccessPremium = isAdmin || hasFeature('lesson.premium') || hasFeature('course.premium');
  const isPremiumProject = project.is_premium === true;
  if (isPremiumProject && !canAccessPremium) {
    return (
      <div className="project-workspace-page" style={{ paddingBottom: 60 }}>
        <Link to="/projects" className="course-detail-back">
          ⬅️ Projelere Dön
        </Link>
        <div style={{ marginTop: 20 }}>
          <PremiumProjectInfoCard
            projectTitle={project.title}
            projectDescription={project.description}
          />
        </div>
      </div>
    );
  }

  // Günlük proje gönderme limiti kontrolü
  const projectAtLimit = isAtLimit('project');
  const cat = getProjectCategory(project);

  // Kategoriye özgü varsayılan kriter etiketleri
  const defaultLabels = {
    science: { quality: 'Bilimsel Doğruluk', logic: 'Hipotez & Metodoloji', readability: 'Çıkarım & Analiz' },
    math: { quality: 'Matematiksel Doğruluk', logic: 'Çözüm Mantığı & Strateji', readability: 'İşlem Adımları & Düzen' },
    english: { quality: 'Gramer & Dilbilgisi', logic: 'Kelime Dağarcığı (Vocab)', readability: 'Akıcılık & Yaratıcılık' },
    turkish: { quality: 'Anlatım & Yaratıcılık', logic: 'İmla & Dil Bilgisi', readability: 'Kurgu & Bütünlük' },
    coding: { quality: 'Kod Kalitesi', logic: 'Algoritma & Mantık', readability: 'Okunabilirlik' },
  }[cat.key] || { quality: 'Kalite', logic: 'Mantık', readability: 'Sunum' };

  const criteria = submitResult?.evaluation.criteriaLabels || defaultLabels;

  return (
    <div className="project-workspace-page">
      <div className="project-workspace-nav-bar">
        <Link to="/projects" className="course-detail-back">
          ⬅️ Projelere Dön
        </Link>
        <div className="project-cat-pill">
          <span>{cat.icon}</span> {cat.label}
        </div>
      </div>

      <header className="project-workspace-header">
        <h1>{project.title}</h1>
        <p>{project.description}</p>
      </header>

      {/* ─── DERS KATEGORİSİNE ÖZEL INTERAKTİF ÇALIŞMA ALANI ─── */}
      {cat.key === 'science' ? (
        <ScienceProjectWorkspace
          initialContent={code}
          projectTitle={project.title}
          projectDescription={project.description}
          onChange={setCode}
          disabled={isSubmitting}
        />
      ) : cat.key === 'math' ? (
        <MathProjectWorkspace
          initialContent={code}
          projectTitle={project.title}
          projectDescription={project.description}
          onChange={setCode}
          disabled={isSubmitting}
        />
      ) : cat.key === 'english' ? (
        <EnglishProjectWorkspace
          initialContent={code}
          projectTitle={project.title}
          projectDescription={project.description}
          onChange={setCode}
          disabled={isSubmitting}
        />
      ) : cat.key === 'turkish' ? (
        <TurkishProjectWorkspace
          initialContent={code}
          projectTitle={project.title}
          projectDescription={project.description}
          onChange={setCode}
          disabled={isSubmitting}
        />
      ) : (
        /* KODLAMA & YAZILIM İÇİN KOD EDİTÖRÜ */
        <div className="grid grid-2 code-editor-grid">
          <CodeEditorPanel
            code={code}
            onChange={setCode}
            onRun={handleRun}
            onReset={handleReset}
            isRunning={isRunning || pyodideLoading}
          />

          <div>
            <OutputPanel
              result={runResult}
              pyodideLoading={pyodideLoading}
              pyodideError={loadError}
              onClear={handleClearOutput}
            />
          </div>
        </div>
      )}

      {/* ─── PROJEYİ AI MENTOR'A TESLİM ETME & DEĞERLENDİRME BÖLÜMÜ ─── */}
      <div className="card project-submit-card" style={{ marginTop: 24 }}>
        <div className="project-submit-inner-header">
          <div>
            <h3>🚀 Projeyi Teslim Et & AI Değerlendirmesi Al</h3>
            <p className="project-submit-desc">
              Çalışmandan memnun olduğunda AI Mentor'a gönder; projen pedagojik kriterlerle incelenip anında puanlansın.
            </p>
          </div>
        </div>

        {/* Proje limiti dolmuşsa engelle */}
        {projectAtLimit && usage ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🚫</div>
            <p style={{ color: '#ef4444', fontWeight: 700, marginBottom: 6 }}>
              Günlük Proje Limitine Ulaştın
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
              {usage.planName} planında günlük en fazla{' '}
              <strong>{usage.projectsLimit}</strong> proje gönderebilirsin.
              Limit yarın sıfırlanır.
            </p>
            <PlanLimitBar show={['project']} />
            <Link to="/pricing" className="lesson-btn primary full" style={{ marginTop: 14, display: 'block' }}>
              💎 Planı Yükselt
            </Link>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="lesson-btn primary full"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳ AI Jüri Projeni Değerlendiriyor...' : '🎯 Projeyi Gönder ve Değerlendir'}
            </button>

            {submitError && <p className="evaluation-error" style={{ marginTop: 12 }}>{submitError}</p>}

            {submitResult && (
              <div className="project-submit-result">
                <div className="project-submit-xp">+{submitResult.xpAwarded} XP Kazandın! ⭐</div>
                <div className="evaluation-scores" style={{ marginTop: 16 }}>
                  <div className="evaluation-score">
                    <div className="evaluation-score-value">{submitResult.evaluation.quality}</div>
                    <div className="evaluation-score-label">{criteria.quality}</div>
                  </div>
                  <div className="evaluation-score">
                    <div className="evaluation-score-value">{submitResult.evaluation.logic}</div>
                    <div className="evaluation-score-label">{criteria.logic}</div>
                  </div>
                  <div className="evaluation-score">
                    <div className="evaluation-score-value">{submitResult.evaluation.readability}</div>
                    <div className="evaluation-score-label">{criteria.readability}</div>
                  </div>
                </div>
                {submitResult.evaluation.overallFeedback && (
                  <p className="evaluation-overall-feedback" style={{ marginTop: 16 }}>
                    {submitResult.evaluation.overallFeedback}
                  </p>
                )}
                {submitResult.evaluation.improvements.length > 0 && (
                  <div className="evaluation-feedback">
                    <h4>💡 Geliştirme ve İlerleme Önerileri</h4>
                    <ul>
                      {submitResult.evaluation.improvements.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

